import {
  DowngradeStrategy,
  IActiveCoupon,
  IAvailablePlan,
  ICurrentSubscription,
  IInvoice,
  IInvoiceList,
  ISubscriptionStatus,
  IUpgradePreview,
} from '../entities/ISubscription';
import { CancellationReasonEnum } from '../enums/CancellationReasonEnum';
import { httpClient } from './helper/httpClient';

type RawSubscriptionDates = {
  [K in keyof ISubscriptionStatus]: ISubscriptionStatus[K] extends Date | null
    ? string | null
    : ISubscriptionStatus[K];
};

type RawActiveCoupon = Omit<IActiveCoupon, 'redeemedAt'> & {
  redeemedAt: string;
};

type RawCurrentSubscription = Omit<
  ICurrentSubscription,
  'subscription' | 'activeCoupon'
> & {
  subscription: RawSubscriptionDates;
  activeCoupon: RawActiveCoupon | null;
};

type RawInvoice = Omit<IInvoice, 'dueDate' | 'paidAt'> & {
  dueDate: string;
  paidAt: string | null;
};

type RawInvoiceList = Omit<IInvoiceList, 'items'> & {
  items: RawInvoice[];
};

interface ICheckoutResponse {
  checkoutUrl: string;
}

interface IPaymentMethodUpdateUrlResponse {
  url: string;
}

type RawUpgradePreview = Omit<IUpgradePreview, 'currentPeriodEnd'> & {
  currentPeriodEnd: string;
};

interface IListPlansResponse {
  plans: IAvailablePlan[];
}

function toDateOrNull(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeActiveCoupon(
  raw: RawActiveCoupon | null,
): IActiveCoupon | null {
  if (!raw) return null;
  return {
    ...raw,
    redeemedAt: new Date(raw.redeemedAt),
  };
}

function normalize(raw: RawCurrentSubscription): ICurrentSubscription {
  return {
    ...raw,
    subscription: {
      ...raw.subscription,
      trialEndsAt: toDateOrNull(raw.subscription.trialEndsAt),
      currentPeriodStart: toDateOrNull(raw.subscription.currentPeriodStart),
      currentPeriodEnd: toDateOrNull(raw.subscription.currentPeriodEnd),
      cancelAt: toDateOrNull(raw.subscription.cancelAt),
    },
    activeCoupon: normalizeActiveCoupon(raw.activeCoupon),
  };
}

function normalizeInvoice(raw: RawInvoice): IInvoice {
  return {
    ...raw,
    dueDate: new Date(raw.dueDate),
    paidAt: toDateOrNull(raw.paidAt),
  };
}

function normalizeInvoiceList(raw: RawInvoiceList): IInvoiceList {
  return {
    ...raw,
    items: raw.items.map(normalizeInvoice),
  };
}

class BillingService {
  private _baseURL = 'billing';

  async getCurrentSubscription(): Promise<ICurrentSubscription> {
    const data = await httpClient.get<null, RawCurrentSubscription>(
      `${this._baseURL}/subscription`,
    );
    return normalize(data);
  }

  async startFreePlan(): Promise<void> {
    await httpClient.post(`${this._baseURL}/start-free`);
  }

  async startTrial(): Promise<void> {
    await httpClient.post(`${this._baseURL}/start-trial`);
  }

  async createCheckout(
    planId: string,
    couponCode?: string,
  ): Promise<ICheckoutResponse> {
    const trimmedCoupon = couponCode?.trim();
    return httpClient.post<
      { planId: string; couponCode?: string },
      ICheckoutResponse
    >(`${this._baseURL}/checkout`, {
      planId,
      ...(trimmedCoupon ? { couponCode: trimmedCoupon } : {}),
    });
  }

  async listInvoices(
    page: number,
    pageSize: number = 20,
  ): Promise<IInvoiceList> {
    const data = await httpClient.get<null, RawInvoiceList>(
      `${this._baseURL}/invoices`,
      {
        params: { page, pageSize },
      },
    );
    return normalizeInvoiceList(data);
  }

  async getPaymentMethodUpdateUrl(): Promise<IPaymentMethodUpdateUrlResponse> {
    return httpClient.get<null, IPaymentMethodUpdateUrlResponse>(
      `${this._baseURL}/payment-method/update-url`,
    );
  }

  async listPlans(): Promise<IAvailablePlan[]> {
    const data = await httpClient.get<null, IListPlansResponse>(
      `${this._baseURL}/plans`,
    );
    return data.plans;
  }

  async previewUpgrade(planId: string): Promise<IUpgradePreview> {
    const raw = await httpClient.post<{ planId: string }, RawUpgradePreview>(
      `${this._baseURL}/upgrade/preview`,
      { planId },
    );
    return {
      ...raw,
      currentPeriodEnd: new Date(raw.currentPeriodEnd),
    };
  }

  async upgrade(planId: string): Promise<void> {
    await httpClient.post<{ planId: string }, void>(
      `${this._baseURL}/upgrade`,
      { planId },
    );
  }

  async downgrade(planId: string, strategy: DowngradeStrategy): Promise<void> {
    await httpClient.post<
      { planId: string; strategy: DowngradeStrategy },
      void
    >(`${this._baseURL}/downgrade`, { planId, strategy });
  }

  async cancelSubscription(
    reason: CancellationReasonEnum,
    comment?: string,
  ): Promise<void> {
    await httpClient.post<
      { reason: CancellationReasonEnum; comment?: string },
      void
    >(`${this._baseURL}/cancel`, { reason, comment });
  }

  async reactivateSubscription(): Promise<void> {
    await httpClient.post<null, void>(`${this._baseURL}/reactivate`);
  }
}

export default new BillingService();
